import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { ColorPicker } from './ui/ColorPicker';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse, getImageUrl } from '../utils/api';

interface ComboBannerFormProps {
  comboBanner?: ComboBanner | null;
  pantallaId: number;
  establishmentId: string;
  availableProducts: ProductReference[];
  bannerDivisions: number; // Divisiones de la pantalla (1 o 2)
  orientation?: 'horizontal' | 'vertical'; // Orientación de la pantalla
  onSuccess: () => void;
  onCancel: () => void;
}

interface ComboBanner {
  id?: number;
  pantalla_id: number;
  producto_id: number;
  title_word1: string;
  title_word1_color: string;
  title_word2: string;
  title_word2_color: string;
  title_font_id?: number | null;
  title_font_size?: number;
  combo_image_path: string;
  combo_image_url?: string | null;
  product_name?: string | null;
  product_name_background_color?: string | null;
  product_name_font_id?: number | null;
  product_name_font_size?: number;
  description?: string | null;
  description_font_id?: number | null;
  description_font_size?: number;
  description_color?: string;
  legend_text: string;
  legend_font_id?: number | null;
  legend_font_size?: number;
  legend_text_color?: string;
  legend_price_color?: string;
  price_color: string;
  price_size: number;
  price_font_id?: number | null;
  background_color: string;
  duration: number;
  display_order: number;
  is_active: number;
}

interface Font {
  id: number;
  name: string;
  display_name: string;
  font_family: string;
}

interface ProductReference {
  reference_code: string;
  base_name: string;
  family?: string | null;
  variants: Array<{
    product_id: number;
    name: string;
    price: number;
    image_url?: string | null;
    description?: string | null;
  }>;
}

export const ComboBannerForm: React.FC<ComboBannerFormProps> = ({
  comboBanner,
  pantallaId,
  establishmentId,
  availableProducts,
  bannerDivisions,
  orientation = 'horizontal',
  onSuccess,
  onCancel
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loadingFonts, setLoadingFonts] = useState(true);
  
  // Flatten products para el select
  const flatProducts = availableProducts.flatMap(ref => 
    ref.variants.map(variant => ({
      id: variant.product_id,
      name: `${ref.base_name} - ${variant.name}`,
      reference: ref.reference_code
    }))
  );

  const [formData, setFormData] = useState({
    producto_id: comboBanner?.producto_id || 0,
    title_word1: comboBanner?.title_word1 || '',
    title_word1_color: comboBanner?.title_word1_color || '#000000',
    title_word2: comboBanner?.title_word2 || '',
    title_word2_color: comboBanner?.title_word2_color || '#000000',
    title_font_id: comboBanner?.title_font_id || null,
    title_font_size: comboBanner?.title_font_size || 48,
    combo_image_path: comboBanner?.combo_image_path || '',
    product_name: comboBanner?.product_name || '',
    product_name_background_color: comboBanner?.product_name_background_color || '',
    product_name_font_id: comboBanner?.product_name_font_id || null,
    product_name_font_size: comboBanner?.product_name_font_size || 24,
    description: comboBanner?.description || '',
    description_font_id: comboBanner?.description_font_id || null,
    description_font_size: comboBanner?.description_font_size || 18,
    description_color: comboBanner?.description_color || '#000000',
    legend_text: comboBanner?.legend_text || 'Hazlo Gigante por:',
    legend_font_id: comboBanner?.legend_font_id || null,
    legend_font_size: comboBanner?.legend_font_size || 16,
    legend_text_color: comboBanner?.legend_text_color || '#000000',
    legend_price_color: comboBanner?.legend_price_color || '#000000',
    price_color: comboBanner?.price_color || '#000000',
    price_size: comboBanner?.price_size || 24,
    price_font_id: comboBanner?.price_font_id || null,
    background_color: comboBanner?.background_color || '#ffffff',
    duration: comboBanner?.duration || 5,
    display_order: comboBanner?.display_order || 0,
    is_active: comboBanner?.is_active ?? 1
  });

  const [imagePreview, setImagePreview] = useState<string | null>(
    comboBanner?.combo_image_url || null
  );
  const [imageFile, setImageFile] = useState<File | null>(null);

  // Cargar fuentes
  useEffect(() => {
    const loadFonts = async () => {
      if (!token) {
        setLoadingFonts(false);
        return;
      }

      setLoadingFonts(true);
      try {
        const fontsRes = await apiFetch('catalog/fonts', {}, token);
        if (!isNetworkErrorResponse(fontsRes)) {
          const fontsJson = await parseJsonResponse(fontsRes);
          if (fontsJson.data?.fonts) {
            setFonts(fontsJson.data.fonts);
          }
        }
      } catch (err: any) {
        console.error('Error cargando fuentes:', err);
        toast.error('Error al cargar fuentes');
      } finally {
        setLoadingFonts(false);
      }
    };

    loadFonts();
  }, [token]);

  useEffect(() => {
    if (comboBanner) {
      setFormData({
        producto_id: comboBanner.producto_id,
        title_word1: comboBanner.title_word1,
        title_word1_color: comboBanner.title_word1_color,
        title_word2: comboBanner.title_word2,
        title_word2_color: comboBanner.title_word2_color,
        title_font_id: comboBanner.title_font_id || null,
        title_font_size: comboBanner.title_font_size || 48,
        combo_image_path: comboBanner.combo_image_path,
        product_name: comboBanner.product_name || '',
        product_name_background_color: comboBanner.product_name_background_color || '',
        product_name_font_id: comboBanner.product_name_font_id || null,
        product_name_font_size: comboBanner.product_name_font_size || 24,
        description: comboBanner.description || '',
        description_font_id: comboBanner.description_font_id || null,
        description_font_size: comboBanner.description_font_size || 18,
        description_color: comboBanner.description_color || '#000000',
        legend_text: comboBanner.legend_text,
        legend_font_id: comboBanner.legend_font_id || null,
        legend_font_size: comboBanner.legend_font_size || 16,
        legend_text_color: comboBanner.legend_text_color || '#000000',
        legend_price_color: comboBanner.legend_price_color || '#000000',
        price_color: comboBanner.price_color,
        price_size: comboBanner.price_size,
        price_font_id: comboBanner.price_font_id || null,
        background_color: comboBanner.background_color,
        duration: comboBanner.duration,
        display_order: comboBanner.display_order,
        is_active: comboBanner.is_active
      });
      setImagePreview(comboBanner.combo_image_url || null);
    }
  }, [comboBanner]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Si se está cambiando el producto_id, auto-rellenar nombre y descripción
    if (name === 'producto_id' && value !== '0') {
      const productId = parseInt(value, 10);
      
      // Buscar el variant completo en availableProducts para obtener nombre y description
      let productName: string | null = null;
      let productDescription: string | null = null;
      for (const ref of availableProducts) {
        const variant = ref.variants.find(v => v.product_id === productId);
        if (variant) {
          productName = variant.name || null;
          productDescription = variant.description || null;
          break;
        }
      }
      
      // Actualizar producto_id, product_name y description
      setFormData(prev => ({
        ...prev,
        producto_id: productId,
        product_name: productName || '',
        description: productDescription || prev.description
      }));
      
      // Limpiar error del campo
      if (fieldErrors[name]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
      
      return; // Salir temprano para evitar el setFormData duplicado
    }
    
    // Limpiar error del campo cuando se modifica
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : parseInt(value, 10)) : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleColorChange = (name: string, value: string) => {
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setFieldErrors(prev => ({
        ...prev,
        combo_image: 'Tipo de archivo no permitido. Solo se permiten: JPEG, PNG, GIF, WEBP'
      }));
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFieldErrors(prev => ({
        ...prev,
        combo_image: 'El archivo excede el tamaño máximo de 5MB'
      }));
      return;
    }

    setImageFile(file);
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.combo_image;
      return newErrors;
    });

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) {
      return formData.combo_image_path || null;
    }

    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    setUploading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('combo_image', imageFile);

      const res = await fetch(
        `${process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api'}.php?route=combo-banners/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        }
      );

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión al subir la imagen');
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        // Convertir error a string si es boolean
        const errorMessage = typeof json.error === 'string' 
          ? json.error 
          : json.error === true 
            ? 'Error al subir la imagen'
            : json.message || 'Error al subir la imagen';
        throw new Error(errorMessage);
      }

      // Acceder correctamente a los datos de la respuesta
      const responseData = (json as any).data || json;
      return responseData.image_path || null;
    } catch (err: any) {
      console.error('Error subiendo imagen:', err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.producto_id || formData.producto_id <= 0) {
      errors.producto_id = 'El producto es requerido';
    }

    if (!formData.title_word1.trim()) {
      errors.title_word1 = 'La primera palabra del título es requerida';
    }

    if (!formData.title_word2.trim()) {
      errors.title_word2 = 'La segunda palabra del título es requerida';
    }

    if (!formData.combo_image_path && !imageFile) {
      errors.combo_image = 'La imagen del combo es requerida';
    }

    // Validar duration solo si bannerDivisions === 1 (slider)
    if (bannerDivisions === 1 && formData.duration < 1) {
      errors.duration = 'La duración debe ser mayor a 0';
    }

    if (formData.price_size < 1) {
      errors.price_size = 'El tamaño del precio debe ser mayor a 0';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('No hay token de autenticación');
      return;
    }

    if (!validateForm()) {
      setError('Por favor, corrige los errores en el formulario');
      return;
    }

    setLoading(true);
    try {
      // Subir imagen si hay una nueva
      let imagePath: string = formData.combo_image_path;
      if (imageFile) {
        const uploadedPath = await uploadImage();
        if (!uploadedPath) {
          throw new Error('Error al subir la imagen');
        }
        imagePath = uploadedPath;
      }

      const payload: any = {
        pantalla_id: pantallaId,
        producto_id: formData.producto_id,
        title_word1: formData.title_word1.trim(),
        title_word1_color: formData.title_word1_color,
        title_word2: formData.title_word2.trim(),
        title_word2_color: formData.title_word2_color,
        title_font_id: formData.title_font_id,
        title_font_size: formData.title_font_size,
        combo_image_path: imagePath,
        product_name: formData.product_name.trim() || null,
        product_name_background_color: formData.product_name_background_color || null,
        product_name_font_id: formData.product_name_font_id,
        product_name_font_size: formData.product_name_font_size,
        description: formData.description.trim() || null,
        description_font_id: formData.description_font_id,
        description_font_size: formData.description_font_size,
        description_color: formData.description_color,
        legend_text: formData.legend_text.trim(),
        legend_font_id: formData.legend_font_id,
        legend_font_size: formData.legend_font_size,
        legend_text_color: formData.legend_text_color,
        legend_price_color: formData.legend_price_color,
        price_color: formData.price_color,
        price_size: formData.price_size,
        price_font_id: formData.price_font_id,
        background_color: formData.background_color,
        duration: bannerDivisions === 1 ? formData.duration : 5, // Solo usar duration si es slider
        display_order: formData.display_order,
        is_active: formData.is_active
      };

      const route = comboBanner ? 'combo-banners/update' : 'combo-banners/create';
      if (comboBanner) {
        payload.id = comboBanner.id;
      }

      const res = await apiFetch(
        route,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        throw new Error('Error de conexión. Verifica tu conexión a internet.');
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al guardar el combo banner';
        throw new Error(errorMessage);
      }

      toast.success(comboBanner ? 'Combo banner actualizado exitosamente' : 'Combo banner creado exitosamente');
      onSuccess();
    } catch (err: any) {
      console.error('Error guardando combo banner:', err);
      const errorMessage = err.message || 'Error inesperado al guardar el combo banner';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {comboBanner ? 'Editar Combo Banner' : 'Nuevo Combo Banner'}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Configura los detalles del combo banner para la pantalla
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Producto */}
        <div>
          <Select
            label="Producto/Combo"
            name="producto_id"
            value={formData.producto_id.toString()}
            onChange={handleChange}
            required
            error={fieldErrors.producto_id}
            options={[
              { value: '0', label: '-- Seleccionar producto --' },
              ...flatProducts.map(product => ({
                value: product.id.toString(),
                label: product.name
              }))
            ]}
          />
        </div>

        {/* Título - Dos palabras */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              type="text"
              label="Primera Palabra del Título"
              name="title_word1"
              value={formData.title_word1}
              onChange={handleChange}
              required
              error={fieldErrors.title_word1}
              placeholder="Ej: Hazlo"
              maxLength={100}
            />
            <div className="mt-2">
              <ColorPicker
                label="Color Primera Palabra"
                value={formData.title_word1_color}
                onChange={(e) => handleColorChange('title_word1_color', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Input
              type="text"
              label="Segunda Palabra del Título"
              name="title_word2"
              value={formData.title_word2}
              onChange={handleChange}
              required
              error={fieldErrors.title_word2}
              placeholder="Ej: Gigante"
              maxLength={100}
            />
            <div className="mt-2">
              <ColorPicker
                label="Color Segunda Palabra"
                value={formData.title_word2_color}
                onChange={(e) => handleColorChange('title_word2_color', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Fuente y tamaño del título */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              label="Fuente del Título"
              name="title_font_id"
              value={formData.title_font_id?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setFormData(prev => ({ ...prev, title_font_id: value }));
              }}
              options={[
                { value: '', label: '-- Usar fuente por defecto --' },
                ...fonts.map(font => ({
                  value: font.id.toString(),
                  label: font.display_name || font.name
                }))
              ]}
              disabled={loadingFonts}
            />
          </div>
          <div>
            <Input
              type="number"
              label="Tamaño del Título (px)"
              name="title_font_size"
              value={formData.title_font_size.toString()}
              onChange={handleChange}
              required
              min={1}
              max={orientation === 'vertical' ? '100' : '72'}
            />
          </div>
        </div>

        {/* Imagen del combo */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Imagen Horizontal del Combo <span className="text-red-500">*</span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleImageChange}
            className="hidden"
          />
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {imageFile ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
            </Button>
            {uploading && <span className="text-sm text-gray-500">Subiendo...</span>}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Formatos permitidos: JPG, PNG, GIF, WEBP. Tamaño máximo: 5MB
          </p>
          {fieldErrors.combo_image && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
              <span>⚠</span>
              {fieldErrors.combo_image}
            </p>
          )}
          {imagePreview && (
            <div className="mt-4">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-w-full h-48 object-contain rounded-lg border border-gray-300 bg-gray-50 p-2"
              />
            </div>
          )}
        </div>

        {/* Nombre del producto */}
        <div>
          <Input
            type="text"
            label="Nombre del Producto"
            name="product_name"
            value={formData.product_name}
            onChange={handleChange}
            placeholder="Se auto-rellena al seleccionar un producto"
            maxLength={255}
            disabled
            className="bg-gray-100 cursor-not-allowed"
          />
          <div className="mt-2">
            <ColorPicker
              label="Color de Fondo del Nombre del Producto"
              value={formData.product_name_background_color || '#ffffff'}
              onChange={(e) => handleColorChange('product_name_background_color', e.target.value)}
            />
          </div>
        </div>

        {/* Fuente y tamaño del nombre del producto */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              label="Fuente del Nombre del Producto"
              name="product_name_font_id"
              value={formData.product_name_font_id?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setFormData(prev => ({ ...prev, product_name_font_id: value }));
              }}
              options={[
                { value: '', label: '-- Usar fuente por defecto --' },
                ...fonts.map(font => ({
                  value: font.id.toString(),
                  label: font.display_name || font.name
                }))
              ]}
              disabled={loadingFonts}
            />
          </div>
          <div>
            <Input
              type="number"
              label="Tamaño del Nombre del Producto (px)"
              name="product_name_font_size"
              value={formData.product_name_font_size.toString()}
              onChange={handleChange}
              required
              min={1}
              max={orientation === 'vertical' ? '100' : '72'}
            />
          </div>
        </div>

        {/* Descripción */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Descripción
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Descripción del combo..."
          />
        </div>

        {/* Fuente y tamaño de la descripción */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              label="Fuente de la Descripción"
              name="description_font_id"
              value={formData.description_font_id?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setFormData(prev => ({ ...prev, description_font_id: value }));
              }}
              options={[
                { value: '', label: '-- Usar fuente por defecto --' },
                ...fonts.map(font => ({
                  value: font.id.toString(),
                  label: font.display_name || font.name
                }))
              ]}
              disabled={loadingFonts}
            />
          </div>
          <div>
            <Input
              type="number"
              label="Tamaño de la Descripción (px)"
              name="description_font_size"
              value={formData.description_font_size.toString()}
              onChange={handleChange}
              required
              min={1}
              max={orientation === 'vertical' ? '100' : '72'}
            />
          </div>
        </div>

        {/* Color de la descripción */}
        <div>
          <ColorPicker
            label="Color de la Descripción"
            value={formData.description_color}
            onChange={(e) => handleColorChange('description_color', e.target.value)}
          />
        </div>

        {/* Leyenda y precio */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              type="text"
              label="Texto de Leyenda"
              name="legend_text"
              value={formData.legend_text}
              onChange={handleChange}
              placeholder="Ej: Hazlo Gigante por:"
              maxLength={255}
            />
          </div>
          <div>
            <Input
              type="number"
              label="Tamaño del Precio (px)"
              name="price_size"
              value={formData.price_size.toString()}
              onChange={handleChange}
              required
              min={1}
              max={orientation === 'vertical' ? '100' : '72'}
              error={fieldErrors.price_size}
            />
            <div className="mt-2">
              <ColorPicker
                label="Color del Precio"
                value={formData.price_color}
                onChange={(e) => handleColorChange('price_color', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Fuente y tamaño de la leyenda */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              label="Fuente de la Leyenda"
              name="legend_font_id"
              value={formData.legend_font_id?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                setFormData(prev => ({ ...prev, legend_font_id: value }));
              }}
              options={[
                { value: '', label: '-- Usar fuente por defecto --' },
                ...fonts.map(font => ({
                  value: font.id.toString(),
                  label: font.display_name || font.name
                }))
              ]}
              disabled={loadingFonts}
            />
          </div>
          <div>
            <Input
              type="number"
              label="Tamaño de la Leyenda (px)"
              name="legend_font_size"
              value={formData.legend_font_size.toString()}
              onChange={handleChange}
              required
              min={1}
              max={orientation === 'vertical' ? '100' : '72'}
            />
          </div>
        </div>

        {/* Colores de la leyenda */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <ColorPicker
              label="Color del Texto de la Leyenda"
              value={formData.legend_text_color}
              onChange={(e) => handleColorChange('legend_text_color', e.target.value)}
            />
          </div>
          <div>
            <ColorPicker
              label="Color del Precio en la Leyenda (Hazlo Gigante por)"
              value={formData.legend_price_color}
              onChange={(e) => handleColorChange('legend_price_color', e.target.value)}
            />
          </div>
        </div>

        {/* Fuente del precio */}
        <div>
          <Select
            label="Fuente del Precio"
            name="price_font_id"
            value={formData.price_font_id?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
              setFormData(prev => ({ ...prev, price_font_id: value }));
            }}
            options={[
              { value: '', label: '-- Usar fuente por defecto --' },
              ...fonts.map(font => ({
                value: font.id.toString(),
                label: font.display_name || font.name
              }))
            ]}
            disabled={loadingFonts}
          />
        </div>

        {/* Configuración de banner */}
        <div className={`grid gap-4 ${bannerDivisions === 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
          <div>
            <ColorPicker
              label="Color de Fondo del Banner"
              value={formData.background_color}
              onChange={(e) => handleColorChange('background_color', e.target.value)}
            />
          </div>
          {bannerDivisions === 1 && (
            <div>
              <Input
                type="number"
                label="Duración (segundos)"
                name="duration"
                value={formData.duration.toString()}
                onChange={handleChange}
                required
                min={1}
                error={fieldErrors.duration}
                helperText="Tiempo que se mostrará este banner en el slider"
              />
            </div>
          )}
          {bannerDivisions === 2 && (
            <Alert variant="info">
              Este banner se mostrará estático en una pantalla dividida. No requiere configuración de duración.
            </Alert>
          )}
        </div>

        {/* Orden y estado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              type="number"
              label="Orden de Visualización"
              name="display_order"
              value={formData.display_order.toString()}
              onChange={handleChange}
              min={0}
              helperText="Orden en el que aparecerá en el slider"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Estado
            </label>
            <select
              name="is_active"
              value={formData.is_active.toString()}
              onChange={handleChange}
              className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="1">Activo</option>
              <option value="0">Inactivo</option>
            </select>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading || uploading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading || uploading}
          >
            {loading ? 'Guardando...' : comboBanner ? 'Actualizar' : 'Crear'}
          </Button>
        </div>
      </form>
    </div>
  );
};

